import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type {
  Appearance,
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from "@stripe/stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRoute } from "wouter";

import {
  type PaymentEventContext,
  paymentInteractionProperties,
  paymentOutcomeProperties,
  readPaidSessionIdFromUrl,
} from "../analytics/paymentEventProperties.js";
import { trackPaymentEvent } from "../analytics/paymentEvents.js";
import { apiClient } from "../api/client.js";

const STATUS_POLL_INTERVAL_MS = 2000;
const BLOCKED_POLL_INTERVAL_MS = 2000;
const FORM_KEEPALIVE_INTERVAL_MS = 3_000;
const PENDING_SLOW_THRESHOLD_MS = 30_000;
const RACE_LOST_NOTICE = "Someone else at this table paid first — you have not been charged.";
const POLL_BACKOFF_AFTER_MS = 60_000;
const POLL_BACKOFF_FACTOR = 1.5;
const POLL_MAX_INTERVAL_MS = 30_000;
// Stay below the platform's default 15s phone-presence freshness window.
const PENDING_MAX_POLL_INTERVAL_MS = 10_000;
const POLL_GIVE_UP_MS = 15 * 60_000;

/**
 * Self-scheduling poll loop: full speed while a live payment would resolve,
 * then gradually backs off to a capped interval so an abandoned tab doesn't
 * hammer the API indefinitely, and gives up entirely after POLL_GIVE_UP_MS.
 * Returns a stop function for effect cleanup.
 */
function startBackoffPoll(options: {
  initialMs: number;
  maxIntervalMs?: number;
  tick: () => Promise<boolean>; // return false to stop polling
  onGiveUp: () => void;
}): () => void {
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let intervalMs = options.initialMs;
  const startedAt = Date.now();

  const run = async () => {
    if (stopped) return;
    if (Date.now() - startedAt > POLL_GIVE_UP_MS) {
      options.onGiveUp();
      return;
    }
    let keepGoing = true;
    try {
      keepGoing = await options.tick();
    } catch {
      // Transient tick failures: keep polling.
    }
    if (stopped || !keepGoing) return;
    if (Date.now() - startedAt > POLL_BACKOFF_AFTER_MS) {
      intervalMs = Math.min(
        intervalMs * POLL_BACKOFF_FACTOR,
        options.maxIntervalMs ?? POLL_MAX_INTERVAL_MS,
      );
    }
    timer = setTimeout(run, intervalMs);
  };

  timer = setTimeout(run, options.initialMs);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

interface PricingOption {
  id: string;
  amount_pence: number;
  duration_minutes: number;
  currency: string;
  sort_order: number;
  badge?: string;
}

interface DeviceInfo {
  device_id: string;
  device_name: string;
  venue_name: string;
  verification_phrase: string;
  pricing_options: PricingOption[];
  default_session_rate_id: string;
  pricing_scope: "fleet" | "venue";
  is_free_play: boolean;
  /** The kiosk app has been cleared to swap its APK and will restart shortly. */
  kiosk_updating: boolean;
  live_session?: { id: string; status: string; ends_at?: string };
  stripe_publishable_key: string;
}

// Read once at module load so StrictMode's double-invoked effects both see it.
const injectedDeviceInfo = readInjectedDeviceInfo();

function readInjectedDeviceInfo(): DeviceInfo | null {
  const raw = window.deviceInfo;
  if (!raw || typeof raw !== "object") return null;
  const candidate = raw as Partial<DeviceInfo>;
  // A hint, not truth: anything shaped wrong is discarded and refetched. Every
  // field the seeded first render touches is checked, because a wrong type here
  // throws during render — a blank page instead of the client-fetch fallback.
  if (typeof candidate.device_id !== "string") return null;
  if (!Array.isArray(candidate.pricing_options)) return null;
  if (typeof candidate.default_session_rate_id !== "string") return null;
  if (typeof candidate.stripe_publishable_key !== "string") return null;
  if (typeof candidate.venue_name !== "string") return null;
  if (typeof candidate.device_name !== "string") return null;
  if (typeof candidate.verification_phrase !== "string") return null;
  if (typeof candidate.is_free_play !== "boolean") return null;
  if (typeof candidate.kiosk_updating !== "boolean") return null;
  if (candidate.pricing_scope !== "fleet" && candidate.pricing_scope !== "venue") {
    candidate.pricing_scope = "fleet";
  }
  return candidate as DeviceInfo;
}

// Tier ids that did not come from a live device-info response — a snapshot, or
// a resumed checkout — are only trusted once the live pricing list still lists
// them; anything else falls back to the table's default.
function knownRateId(info: DeviceInfo, rateId: string | null | undefined): string | null {
  if (!rateId) return null;
  return info.pricing_options.some((opt) => opt.id === rateId) ? rateId : null;
}

interface Checkout {
  checkoutId: string;
  sessionId: string;
  clientSecret: string;
  verificationCode: number;
  mode: "new" | "extension";
  sessionRateId: string;
}

interface CheckoutStatus {
  checkout_id: string;
  status:
    | "pending"
    | "authorized"
    | "capturing"
    | "succeeded"
    | "failed"
    | "cancelled"
    | "refund_due"
    | "refund_failed_manual"
    | "refunded";
  session_id: string;
  session_rate_id?: string;
  cancelled_reason?: string;
  session_status: string;
  session_ends_at?: string;
  amount_pence: number;
  duration_minutes: number;
  currency: string;
  verification_code: number;
  verification_phrase: string;
  precondition_ok: boolean;
  pricing_scope: "fleet" | "venue";
}

type PayPhase =
  | { phase: "loading" }
  // `retryable` is for failures a reload can clear (a transient server error, a
  // lost network). A bad link or an unconfigured table is not one of them, and
  // offering a button that cannot work is worse than offering none.
  | { phase: "fatal"; message: string; retryable?: boolean }
  | { phase: "free_play" }
  | { phase: "blocked"; reason: "activating" | "too_many_checkouts" | "updating" }
  | { phase: "form"; checkout: Checkout }
  | { phase: "pending"; checkout: Checkout }
  | { phase: "success"; isExtension: boolean; verificationPhrase: string; verificationCode: number }
  | { phase: "failed"; title: string; message: string };

function checkoutStorageKey(deviceId: string): string {
  return `tt-checkout:${deviceId}`;
}

interface StoredCheckout {
  token: string;
  checkoutId: string | null;
  mode: "new" | "extension" | null;
}

function loadStoredCheckout(deviceId: string): StoredCheckout {
  try {
    const raw = sessionStorage.getItem(checkoutStorageKey(deviceId));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<StoredCheckout>;
      if (parsed.token) {
        return {
          token: parsed.token,
          checkoutId: parsed.checkoutId ?? null,
          mode: parsed.mode ?? null,
        };
      }
    }
  } catch {
    // Corrupt storage falls through to a fresh token.
  }
  return { token: crypto.randomUUID(), checkoutId: null, mode: null };
}

function storeCheckout(
  deviceId: string,
  token: string,
  checkoutId: string | null,
  mode: "new" | "extension" | null,
): void {
  try {
    sessionStorage.setItem(
      checkoutStorageKey(deviceId),
      JSON.stringify({ token, checkoutId, mode }),
    );
  } catch {
    // Private-mode storage failures only cost refresh-resume.
  }
}

const appearance: Appearance = {
  theme: "night",
  variables: {
    colorPrimary: "#6b4dff",
    colorBackground: "#1a1b26",
    colorText: "#ffffff",
    borderRadius: "12px",
    fontFamily: "Exo 2, system-ui, sans-serif",
  },
};

/** iOS / Android: Apple Pay / Google Pay via Express Checkout. Desktop & other UAs: card-only. */
function getMobileExpressCheckoutOptions() {
  if (typeof navigator === "undefined") {
    return null;
  }
  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  if (!isIos && !isAndroid) {
    return null;
  }
  const paymentMethodOrder = isIos ? ["apple_pay"] : ["google_pay"];
  return {
    paymentMethodOrder,
    layout: { overflow: "auto" as const },
    paymentMethods: isIos
      ? ({
          applePay: "always",
          googlePay: "never",
          link: "never",
        } as const)
      : ({
          googlePay: "always",
          applePay: "never",
          link: "never",
        } as const),
  };
}

const paymentElementOptions = {
  layout: {
    type: "accordion" as const,
    defaultCollapsed: false,
    spacedAccordionItems: true,
  },
  /** Card-only on desktop; on mobile, wallets use Express Checkout (no duplicate buttons). */
  paymentMethodOrder: ["card"] as string[],
  wallets: {
    applePay: "never" as const,
    googlePay: "never" as const,
  },
};

function formatPrice(pence: number, currency: string): string {
  const sym = currency.toLowerCase() === "gbp" ? "£" : "";
  return `${sym}${(pence / 100).toFixed(2)}`;
}

/** Badge strings from `session_rates.badge` in the API / seed. */
function badgeImageSrc(badge: string | undefined): string | null {
  if (badge === "popular") return "/flag-popular.png";
  if (badge === "best_value") return "/flag-best-value.png";
  return null;
}

function Spinner() {
  return (
    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
  );
}

function PaymentForm({
  amountPence,
  currency,
  amountVersion,
  onAuthorized,
  onStripeError,
  onProcessingChange,
  trackInteraction,
}: {
  amountPence: number;
  currency: string;
  amountVersion: number;
  onAuthorized: () => void;
  onStripeError: (message: string) => void;
  onProcessingChange: (processing: boolean) => void;
  trackInteraction: (interactionType: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessingState] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The parent locks the tier selection while a payment attempt is in flight.
  const setProcessing = useCallback(
    (value: boolean) => {
      setProcessingState(value);
      onProcessingChange(value);
    },
    [onProcessingChange],
  );

  // Tier changes update the PaymentIntent amount server-side; Elements must re-fetch it.
  useEffect(() => {
    if (amountVersion > 0) {
      void elements?.fetchUpdates();
    }
  }, [elements, amountVersion]);

  const confirm = useCallback(
    async (paymentFailed?: StripeExpressCheckoutElementConfirmEvent["paymentFailed"]) => {
      if (!stripe || !elements) {
        paymentFailed?.({ reason: "invalid_payment_data" });
        return;
      }
      setProcessing(true);
      setError(null);

      const { error: stripeError } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });

      if (stripeError) {
        setError(stripeError.message ?? "Payment failed. Please try again.");
        onStripeError(stripeError.message ?? "unknown stripe error");
        setProcessing(false);
        paymentFailed?.({ reason: "invalid_payment_data" });
        return;
      }

      // Any non-error outcome (requires_capture, processing, ...) heads to the pending
      // screen; the server-side status poll is the single source of truth from here.
      onAuthorized();
    },
    [stripe, elements, onAuthorized, onStripeError, setProcessing],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      trackInteraction("card_submit_pressed");
      await confirm();
    },
    [confirm, trackInteraction],
  );

  const handleExpressConfirm = useCallback(
    async ({ paymentFailed }: StripeExpressCheckoutElementConfirmEvent) => {
      trackInteraction("express_checkout_pressed");
      await confirm(paymentFailed);
    },
    [confirm, trackInteraction],
  );

  // The price is locked from the moment the wallet sheet opens — the sheet
  // shows an amount, so a tier switch underneath it must be impossible.
  const handleExpressClick = useCallback(
    (event: StripeExpressCheckoutElementClickEvent) => {
      setProcessing(true);
      event.resolve();
    },
    [setProcessing],
  );

  const handleExpressCancel = useCallback(() => {
    setProcessing(false);
  }, [setProcessing]);

  const expressCheckoutOptions = useMemo(() => getMobileExpressCheckoutOptions(), []);

  const payButton = (
    <button
      type="submit"
      disabled={!stripe || processing}
      className="w-full py-4 rounded-xl font-sans font-bold text-lg text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        background: "linear-gradient(180deg, #4db9ff 0%, #0051c9 100%)",
        boxShadow: "0 4px 20px rgba(107, 77, 255, 0.35)",
      }}
    >
      {processing ? "Processing..." : `Pay ${formatPrice(amountPence, currency)}`}
    </button>
  );

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {expressCheckoutOptions ? (
        <>
          <div className="express-checkout-wrap w-full min-h-[2.75rem]">
            <ExpressCheckoutElement
              options={expressCheckoutOptions}
              onClick={handleExpressClick}
              onCancel={handleExpressCancel}
              onConfirm={handleExpressConfirm}
            />
          </div>

          {error ? <p className="text-red-400 text-sm text-center font-sans">{error}</p> : null}

          <div className="flex items-center gap-3 text-text-muted text-xs font-sans uppercase tracking-wider">
            <span className="h-px flex-1 bg-border" aria-hidden />
            <span>or</span>
            <span className="h-px flex-1 bg-border" aria-hidden />
          </div>

          <details className="group rounded-xl border border-border bg-surface-raised/40 open:border-border-subtle">
            <summary className="cursor-pointer list-none px-4 py-3 text-sm text-text-secondary font-sans marker:content-none [&::-webkit-details-marker]:hidden flex items-center justify-between gap-2">
              <span>Other payment methods</span>
              <span className="text-text-muted text-xs shrink-0 group-open:rotate-180 transition-transform">
                ▼
              </span>
            </summary>
            <div className="px-4 pb-4 pt-0 flex flex-col gap-4 border-t border-border/60">
              <PaymentElement options={{ ...paymentElementOptions }} />
              {payButton}
            </div>
          </details>
        </>
      ) : (
        <>
          <PaymentElement options={{ ...paymentElementOptions }} />
          {error ? <p className="text-red-400 text-sm text-center font-sans">{error}</p> : null}
          {payButton}
        </>
      )}
    </form>
  );
}

function EmulatorPaymentForm({
  amountPence,
  currency,
  clientSecret,
  layoutPreview,
  onAuthorized,
  onStripeError,
  onProcessingChange,
  trackInteraction,
}: {
  amountPence: number;
  currency: string;
  clientSecret: string;
  layoutPreview: boolean;
  onAuthorized: () => void;
  onStripeError: (message: string) => void;
  onProcessingChange: (processing: boolean) => void;
  trackInteraction: (interactionType: string) => void;
}) {
  const [processing, setProcessingState] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isLayoutPreview = layoutPreview || clientSecret.startsWith("pi_preview_");

  const setProcessing = useCallback(
    (value: boolean) => {
      setProcessingState(value);
      onProcessingChange(value);
    },
    [onProcessingChange],
  );

  const handleSimulatePayment = useCallback(async () => {
    if (isLayoutPreview) return;
    setProcessing(true);
    setError(null);
    trackInteraction("emulator_pay_pressed");

    const piId = clientSecret.split("_secret_")[0];

    try {
      const res = await fetch(`/api/stripe-emulator/confirm/${piId}`, { method: "POST" });
      if (!res.ok) {
        setError("Simulated payment failed. Check the stripe emulator.");
        onStripeError("Stripe emulator confirmation failed");
        setProcessing(false);
        return;
      }
      onAuthorized();
    } catch {
      setError("Could not reach stripe emulator.");
      onStripeError("Could not reach stripe emulator");
      setProcessing(false);
    }
  }, [clientSecret, isLayoutPreview, onAuthorized, onStripeError, setProcessing, trackInteraction]);

  return (
    <div className="flex flex-col gap-4">
      <div
        className="rounded-2xl border p-4 font-sans"
        style={{
          borderColor: "rgba(131, 149, 234, 0.5)",
          background:
            "linear-gradient(180deg, rgba(120, 130, 223, 0.18) 0%, rgba(0, 0, 121, 0.35) 100%)",
        }}
      >
        <p className="text-sm font-semibold text-accent mb-3">Apple Pay</p>
        <p className="text-xs text-text-secondary leading-relaxed mb-4">
          Another step will appear to securely submit your payment information.
        </p>

        {isLayoutPreview && (
          <p className="text-yellow-200/90 text-xs mb-3">
            Layout preview — wallets and card fields are cosmetic until a real PaymentIntent loads
            on non-emulator builds.
          </p>
        )}

        {!isLayoutPreview && (
          <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-xl p-3 text-center mb-4">
            <p className="text-yellow-300 text-xs uppercase tracking-widest font-bold">
              Stripe Emulator Mode
            </p>
            <p className="text-yellow-200/70 text-xs mt-1">Payment will be simulated locally</p>
          </div>
        )}

        {error && <p className="text-red-400 text-sm text-center font-sans">{error}</p>}

        <button
          type="button"
          data-testid="emulator-pay-button"
          disabled={processing || isLayoutPreview}
          onClick={handleSimulatePayment}
          className="w-full py-4 rounded-xl font-sans font-bold text-lg text-white transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(180deg, #4db9ff 0%, #0051c9 100%)",
            boxShadow: "0 4px 20px rgba(107, 77, 255, 0.35)",
          }}
        >
          {isLayoutPreview
            ? `Pay ${formatPrice(amountPence, currency)}`
            : processing
              ? "Processing..."
              : `Simulate Payment — ${formatPrice(amountPence, currency)}`}
        </button>

        <p className="text-center text-text-muted text-xs mt-4 flex items-center justify-center gap-2">
          <span aria-hidden>💳</span>
          Other payment options
        </p>
      </div>
    </div>
  );
}

const MOCK_PRICING: PricingOption[] = [
  {
    id: "00000000-0000-4000-8000-000000000201",
    amount_pence: 299,
    duration_minutes: 15,
    currency: "gbp",
    sort_order: 0,
  },
  {
    id: "00000000-0000-4000-8000-000000000202",
    amount_pence: 499,
    duration_minutes: 30,
    currency: "gbp",
    sort_order: 1,
    badge: "popular",
  },
  {
    id: "00000000-0000-4000-8000-000000000203",
    amount_pence: 799,
    duration_minutes: 60,
    currency: "gbp",
    sort_order: 2,
    badge: "best_value",
  },
];

const MOCK_DEVICE_INFO: DeviceInfo = {
  device_id: "00000000-0000-4000-8000-000000000099",
  venue_name: "The Red Lion",
  device_name: "Table number 1",
  verification_phrase: "Bold Dragon",
  pricing_options: MOCK_PRICING,
  default_session_rate_id: "00000000-0000-4000-8000-000000000202",
  pricing_scope: "fleet",
  is_free_play: false,
  kiosk_updating: false,
  stripe_publishable_key: "pk_emulator_local_key",
};

function isPayPreviewMode(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  if (host !== "localhost" && host !== "127.0.0.1") return false;
  return new URLSearchParams(window.location.search).has("preview");
}

export function Pay() {
  const [, params] = useRoute("/pay/:deviceId");
  const [, sessionParams] = useRoute("/pay/session/:sessionId");
  const deviceId = params?.deviceId;
  const sessionId = sessionParams?.sessionId;
  const linkKey = sessionId ? `session:${sessionId}` : deviceId;
  const preview = isPayPreviewMode();
  const paidSessionIdFromUrl = useMemo(() => sessionId ?? readPaidSessionIdFromUrl(), [sessionId]);

  // A free-play table has no price list to paint, so seeding it would flash
  // three priced tiers before the page settles on the free-play screen.
  const seeded =
    injectedDeviceInfo &&
    (sessionId
      ? injectedDeviceInfo.live_session?.id === sessionId
      : injectedDeviceInfo.device_id === deviceId) &&
    !injectedDeviceInfo.is_free_play
      ? injectedDeviceInfo
      : null;
  const [phase, setPhase] = useState<PayPhase>({ phase: "loading" });
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(seeded);
  const [selectedRateId, setSelectedRateId] = useState<string | null>(
    seeded?.default_session_rate_id ?? null,
  );
  const [rateSwitching, setRateSwitching] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);
  const [paying, setPaying] = useState(false);
  const [amountVersion, setAmountVersion] = useState(0);
  const [pendingSlow, setPendingSlow] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const tokenRef = useRef<string>("");
  // A tier the payer picked while no checkout existed yet. The claim prefers it,
  // and whatever the claim came back with is reconciled against it on entering
  // the form phase, so a tap in that window is never silently dropped.
  const chosenRateRef = useRef<string | null>(null);

  const stripeKey = deviceInfo?.stripe_publishable_key ?? "";
  const isEmulatorMode = stripeKey.startsWith("pk_emulator_");
  const stripePromise = useMemo(
    () => (stripeKey && !isEmulatorMode ? loadStripe(stripeKey) : null),
    [stripeKey, isEmulatorMode],
  );

  // Every event carries the interactive session id (the shared play session)
  // plus this payer's own checkout id — distinct payment attempts stay
  // distinguishable within one table interaction.
  const eventContext = useCallback(
    (interactiveSessionId: string, checkoutId: string | null): PaymentEventContext => ({
      interactiveSessionId,
      checkoutId,
      paidSessionIdFromUrl,
    }),
    [paidSessionIdFromUrl],
  );

  const trackInteraction = useCallback(
    (
      sessionId: string,
      checkoutId: string | null,
      interactionType: string,
      extra: Record<string, unknown> = {},
    ) => {
      void trackPaymentEvent(
        sessionId,
        "screen_interacted",
        paymentInteractionProperties(eventContext(sessionId, checkoutId), interactionType, extra),
      );
    },
    [eventContext],
  );

  const fetchPaymentInfo = useCallback(
    () =>
      sessionId
        ? apiClient["/api/v1/user/play-sessions/{id}"]["get"]({
            parameters: { id: sessionId },
            body: null,
          })
        : apiClient["/api/v1/user/devices/{deviceId}"]["get"]({
            parameters: { deviceId: deviceId! },
            body: null,
          }),
    [sessionId, deviceId],
  );

  // Attribute entry before any client info lookup or checkout/Stripe round trip.
  const openedSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sessionId || preview || openedSessionRef.current === sessionId) return;
    openedSessionRef.current = sessionId;
    trackInteraction(sessionId, null, "page_opened");
  }, [sessionId, preview, trackInteraction]);

  // The outcome lets the blocked-screen poll distinguish transient conflicts
  // (keep polling) from phases that end the poll.
  type ClaimOutcome = "claimed" | "conflict" | "free_play" | "fatal";

  const claimCheckout = useCallback(
    async (rateId: string): Promise<ClaimOutcome> => {
      const claimBody = { session_rate_id: rateId, checkout_token: tokenRef.current };
      const res = sessionId
        ? await apiClient["/api/v1/user/play-sessions/{id}/checkout"]["post"]({
            parameters: { id: sessionId },
            body: claimBody,
          })
        : await apiClient["/api/v1/user/devices/{deviceId}/checkout"]["post"]({
            parameters: { deviceId: deviceId! },
            body: claimBody,
          });

      if (res.code === 201) {
        const body = res.body as {
          checkout_id: string;
          session_id: string;
          client_secret: string;
          verification_code: number;
          mode: "new" | "extension";
          session_rate_id: string;
          amount_pence: number;
        };
        storeCheckout(linkKey!, tokenRef.current, body.checkout_id, body.mode);
        setSelectedRateId(body.session_rate_id || rateId);
        setPhase({
          phase: "form",
          checkout: {
            checkoutId: body.checkout_id,
            sessionId: body.session_id,
            clientSecret: body.client_secret,
            verificationCode: body.verification_code,
            mode: body.mode,
            sessionRateId: body.session_rate_id || rateId,
          },
        });
        if (!sessionId)
          trackInteraction(body.session_id, body.checkout_id, "page_opened", {
            interaction_target: body.mode === "extension" ? "extension" : "new_session",
            amount_pence: body.amount_pence,
          });
        return "claimed";
      }

      if (res.code === 409) {
        const body = res.body as { reason?: string; message?: string };
        if (body.reason === "free_play") {
          setPhase({ phase: "free_play" });
          return "free_play";
        }
        if (
          body.reason === "activating" ||
          body.reason === "too_many_checkouts" ||
          body.reason === "updating"
        ) {
          setPhase({ phase: "blocked", reason: body.reason });
          return "conflict";
        }
        // Anything else — `attach_failed` today — is not a state that waiting
        // will clear, but it is a transient server-side race, so surface it with
        // a way out rather than parking the payer on a screen that never updates.
        setPhase({
          phase: "fatal",
          message: body.message ?? "Could not start a payment. Please try again.",
          retryable: true,
        });
        return "fatal";
      }

      if (res.code === 404) {
        setPhase({
          phase: "fatal",
          message: sessionId
            ? "This table session has ended or the link is invalid. Please scan the table’s QR code again."
            : "Device not found. This link may be invalid.",
        });
        return "fatal";
      }

      const body = res.body as { message?: string };
      setPhase({
        phase: "fatal",
        message: body?.message ?? "Could not start a payment. Please try again.",
        retryable: true,
      });
      return "fatal";
    },
    [deviceId, sessionId, linkKey, trackInteraction],
  );

  // A lost race is not a dead end: drop the checkout, then land wherever a
  // fresh load would put this payer (unlocked table, extension, free play).
  const rebootstrapAfterLostRace = useCallback(
    async (interactiveSessionId: string, checkoutId: string | null) => {
      try {
        trackInteraction(interactiveSessionId, checkoutId, "race_lost_rebootstrap");
        setNotice(RACE_LOST_NOTICE);
        setPaying(false);
        setPhase({ phase: "loading" });

        const res = await fetchPaymentInfo();
        if (res.code !== 200) {
          setPhase({
            phase: "fatal",
            message: "Something went wrong. Please try again.",
            retryable: true,
          });
          return;
        }
        const info = res.body as DeviceInfo;
        setDeviceInfo(info);
        if (info.is_free_play) {
          setPhase({ phase: "free_play" });
          return;
        }
        if (info.live_session?.status === "activating") {
          setPhase({ phase: "blocked", reason: "activating" });
          return;
        }
        if (info.kiosk_updating) {
          setPhase({ phase: "blocked", reason: "updating" });
          return;
        }
        const claimRateId =
          knownRateId(info, chosenRateRef.current) ?? info.default_session_rate_id;
        setSelectedRateId(claimRateId);
        await claimCheckout(claimRateId);
      } catch (err) {
        // The payer only ever sees the generic fatal screen, so this log is the
        // sole record of why a lost-race recovery failed.
        console.error("Re-bootstrap after a lost race failed:", err);
        setPhase({
          phase: "fatal",
          message: "Something went wrong. Please try again.",
          retryable: true,
        });
      }
    },
    [fetchPaymentInfo, claimCheckout, trackInteraction],
  );

  // Bootstrap: device info, then resume a stored checkout or claim a fresh one.
  useEffect(() => {
    if (!linkKey) {
      setPhase({ phase: "fatal", message: "Invalid link" });
      return;
    }

    if (preview) {
      setDeviceInfo(MOCK_DEVICE_INFO);
      setSelectedRateId(MOCK_DEVICE_INFO.default_session_rate_id);
      tokenRef.current = "preview-token";
      setPhase({
        phase: "form",
        checkout: {
          checkoutId: "preview-checkout-id",
          sessionId: "preview-session-id",
          clientSecret: "pi_preview_fake_secret_fake",
          verificationCode: 42,
          mode: "new",
          sessionRateId: MOCK_DEVICE_INFO.default_session_rate_id,
        },
      });
      return;
    }

    let cancelled = false;
    const stored = loadStoredCheckout(linkKey);
    tokenRef.current = stored.token;
    storeCheckout(linkKey, stored.token, stored.checkoutId, stored.mode);

    async function bootstrap() {
      const deviceRes = await fetchPaymentInfo();

      if (cancelled) return;

      if (deviceRes.code === 404) {
        setPhase({
          phase: "fatal",
          message: sessionId
            ? "This table session has ended or the link is invalid. Please scan the table’s QR code again."
            : "Device not found. This link may be invalid.",
        });
        return;
      }
      if (deviceRes.code !== 200) {
        setPhase({
          phase: "fatal",
          message: "Something went wrong. Please try again.",
          retryable: true,
        });
        return;
      }

      const info = deviceRes.body as DeviceInfo;
      setDeviceInfo(info);

      if (!info.stripe_publishable_key) {
        setPhase({
          phase: "fatal",
          message: "This table is not set up for payments yet. Please ask a member of staff.",
        });
        return;
      }

      if (!info.pricing_options.length || !info.default_session_rate_id) {
        setPhase({
          phase: "fatal",
          message: "No pricing is available right now. Please try again later.",
        });
        return;
      }

      // Resume a checkout from a refresh or redirect-return before claiming anew.
      let resumeRateId: string | null = null;
      if (stored.checkoutId) {
        // Repair a lost authorization nudge first (idempotent server-side).
        await apiClient["/api/v1/user/checkouts/{id}/authorized"]
          ["post"]({
            parameters: { id: stored.checkoutId },
            body: { checkout_token: tokenRef.current },
          })
          .catch(() => null);

        const statusRes = await apiClient["/api/v1/user/checkouts/{id}"]["get"]({
          parameters: { id: stored.checkoutId, checkout_token: tokenRef.current },
          body: null,
        });

        if (cancelled) return;

        if (statusRes.code === 200) {
          const status = statusRes.body as CheckoutStatus;
          if (status.status === "authorized" || status.status === "capturing") {
            setPhase({
              phase: "pending",
              checkout: {
                checkoutId: status.checkout_id,
                sessionId: status.session_id,
                clientSecret: "",
                verificationCode: status.verification_code,
                mode: stored.mode ?? (status.session_status === "active" ? "extension" : "new"),
                sessionRateId: "",
              },
            });
            return;
          }
          if (status.status === "succeeded") {
            setPhase({
              phase: "success",
              isExtension: stored.mode === "extension",
              verificationPhrase: status.verification_phrase,
              verificationCode: status.verification_code,
            });
            return;
          }
          // pending → re-claim below to recover the client_secret, keeping the
          // tier the payer had already chosen; failed/cancelled/refunded →
          // fresh claim below at the default tier.
          if (status.status === "pending") {
            resumeRateId = knownRateId(info, status.session_rate_id);
          }
        }
      }

      if (info.is_free_play) {
        setPhase({ phase: "free_play" });
        return;
      }
      if (info.kiosk_updating) {
        setPhase({ phase: "blocked", reason: "updating" });
        return;
      }

      const claimRateId =
        resumeRateId ?? knownRateId(info, chosenRateRef.current) ?? info.default_session_rate_id;
      setSelectedRateId(claimRateId);
      await claimCheckout(claimRateId);
    }

    bootstrap().catch(() => {
      if (!cancelled) {
        setPhase({
          phase: "fatal",
          message: "Network error. Please check your connection.",
          retryable: true,
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [linkKey, sessionId, preview, fetchPaymentInfo, claimCheckout]);

  // Blocked: record the funnel drop against the interactive session, then
  // poll until the table is ready.
  useEffect(() => {
    if (phase.phase !== "blocked" || preview) return;
    const liveSessionId = deviceInfo?.live_session?.id;
    if (liveSessionId) {
      trackInteraction(liveSessionId, null, "payment_slot_blocked", {
        interaction_target: phase.reason,
      });
    }
    // Keyed on the phase only: one event per blocked entry.
  }, [phase.phase, preview]);

  // Blocked: poll device info until the table is ready to accept checkouts.
  useEffect(() => {
    if (phase.phase !== "blocked" || !linkKey || preview) return;

    const stop = startBackoffPoll({
      initialMs: BLOCKED_POLL_INTERVAL_MS,
      tick: async () => {
        const res = await fetchPaymentInfo();
        if (res.code !== 200) return true;
        const info = res.body as DeviceInfo;
        setDeviceInfo(info);
        if (info.is_free_play) {
          setPhase({ phase: "free_play" });
          return false;
        }
        // Both clear on their own: activation resolves within the ack timeout,
        // and the update hold lifts when the new kiosk build reports in.
        const waiting = info.live_session?.status === "activating" || info.kiosk_updating;
        if (!waiting) {
          const claimRateId =
            knownRateId(info, chosenRateRef.current) ?? info.default_session_rate_id;
          setSelectedRateId(claimRateId);
          const outcome = await claimCheckout(claimRateId);
          // A conflict means the table is still blocked: we are back on the blocked
          // screen with the same phase.phase, so this effect will NOT restart — keep
          // this poll alive.
          return outcome === "conflict";
        }
        return true;
      },
      onGiveUp: () => {
        setPhase({
          phase: "failed",
          title: "Table still busy",
          message:
            "This table has been busy for a while, so we've stopped checking. " +
            "You have NOT been charged — refresh this page to check again.",
        });
      },
    });

    return stop;
  }, [phase.phase, linkKey, preview, fetchPaymentInfo, claimCheckout]);

  // Form: poll the checkout status while the payer fills in card details. This
  // touches the checkout's updated_at server-side (keeping the stale-eviction
  // sweep from cancelling it), detects when this payer has lost the race (so the
  // pay control tears down before they can authorize), notices when another tab
  // authorized this checkout (follow it to pending), and reclaims when the checkout
  // died for other reasons (session ended, cancelled by server) to avoid a cryptic
  // Stripe error from the pay button.
  useEffect(() => {
    if (phase.phase !== "form" || preview) return;
    const { checkout } = phase;
    let cancelled = false;
    let inFlight = false;

    const interval = setInterval(async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const res = await apiClient["/api/v1/user/checkouts/{id}"]["get"]({
          parameters: { id: checkout.checkoutId, checkout_token: tokenRef.current },
          body: null,
        });
        // The GET can return after this effect has cleaned up (e.g. the payer
        // confirmed and moved to pending). Applying stale results would tear
        // down the wrong screen or reclaim when the race is already won.
        if (cancelled) return;
        if (res.code !== 200) return;
        const status = res.body as CheckoutStatus;
        // A lost race reaches this poll two ways. The common one is the self-healing
        // GET, which cancels the row as precondition_failed and returns it that way.
        // The rare one is a still-pending row the conditional cancel could not claim
        // because the sweep got there first. Both mean the same thing to this payer.
        const lostRace =
          (status.status === "cancelled" && status.cancelled_reason === "precondition_failed") ||
          (status.status === "pending" && !status.precondition_ok);
        if (lostRace) {
          // Tear down before anything else — the pay control must not survive long
          // enough for a loser to authorize into a race they have already lost.
          await rebootstrapAfterLostRace(checkout.sessionId, checkout.checkoutId);
          return;
        }
        switch (status.status) {
          case "pending":
            return; // still ours; the read was the keepalive
          case "authorized":
          case "capturing":
            // Confirmed from another tab of the same payer: follow it.
            setPhase({ phase: "pending", checkout });
            return;
          case "succeeded":
            setPhase({
              phase: "success",
              isExtension: checkout.mode === "extension",
              verificationPhrase: status.verification_phrase,
              verificationCode: status.verification_code,
            });
            return;
          default: {
            // This checkout died: reclaim at the selected tier.
            const rateId =
              selectedRateId || checkout.sessionRateId || deviceInfo?.default_session_rate_id;
            if (rateId) {
              await claimCheckout(rateId);
            }
          }
        }
      } catch {
        // Transient failure: the next tick retries.
      } finally {
        inFlight = false;
      }
    }, FORM_KEEPALIVE_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [phase, preview, selectedRateId, deviceInfo, claimCheckout, rebootstrapAfterLostRace]);

  // Pending: poll the checkout status until it reaches a terminal state.
  useEffect(() => {
    if (phase.phase !== "pending" || preview) return;

    const { checkout } = phase;
    const startedAt = Date.now();
    setPendingSlow(false);

    trackInteraction(checkout.sessionId, checkout.checkoutId, "payment_pending_shown");

    // The nudge is the only push signal to the server (there is no Stripe
    // webhook), so it is retried on every tick until the server has registered
    // the authorization. Symmetric by construction: any tick that could show
    // the customer an outcome could also have delivered the signal.
    let serverHasAuthorization = false;

    const stop = startBackoffPoll({
      initialMs: STATUS_POLL_INTERVAL_MS,
      maxIntervalMs: PENDING_MAX_POLL_INTERVAL_MS,
      tick: async () => {
        if (Date.now() - startedAt > PENDING_SLOW_THRESHOLD_MS) {
          setPendingSlow(true);
        }

        if (!serverHasAuthorization) {
          await apiClient["/api/v1/user/checkouts/{id}/authorized"]
            ["post"]({
              parameters: { id: checkout.checkoutId },
              body: { checkout_token: tokenRef.current },
            })
            .catch(() => null);
        }

        const res = await apiClient["/api/v1/user/checkouts/{id}"]["get"]({
          parameters: { id: checkout.checkoutId, checkout_token: tokenRef.current },
          body: null,
        });
        if (res.code !== 200) return true;
        const status = res.body as CheckoutStatus;
        serverHasAuthorization = status.status !== "pending";

        // Server-truth money fields from the payment row (reconciled against the
        // PaymentIntent at authorization) — the amounts analytics can trust.
        const outcomeMoney = {
          amount_pence: status.amount_pence,
          currency: status.currency,
          duration_minutes: status.duration_minutes,
          session_rate_id: status.session_rate_id ?? null,
          pricing_scope: status.pricing_scope,
        };

        switch (status.status) {
          case "succeeded":
            void trackPaymentEvent(
              checkout.sessionId,
              "payment_confirmed",
              paymentOutcomeProperties(eventContext(checkout.sessionId, checkout.checkoutId), {
                mode: checkout.mode,
                ...outcomeMoney,
              }),
            );
            setPhase({
              phase: "success",
              isExtension: checkout.mode === "extension",
              verificationPhrase: status.verification_phrase,
              verificationCode: status.verification_code,
            });
            return false;
          case "cancelled": {
            // An ack timeout (table never responded) and e.g. someone pressing
            // "end session" on the table are different outcomes — for both the
            // customer and the analytics funnel.
            const ackTimeout = status.cancelled_reason === "ack_timeout";
            void trackPaymentEvent(
              checkout.sessionId,
              ackTimeout ? "payment_ack_timeout" : "payment_cancelled",
              paymentOutcomeProperties(eventContext(checkout.sessionId, checkout.checkoutId), {
                ...(ackTimeout ? {} : { reason: status.cancelled_reason ?? "unknown" }),
                ...outcomeMoney,
              }),
            );
            setPhase({
              phase: "failed",
              title: "Payment not completed",
              message: ackTimeout
                ? "The table didn't respond, so we cancelled the payment. " +
                  "You have NOT been charged. Please check the table is on and try again, " +
                  "or ask a member of staff."
                : status.cancelled_reason === "session_ended" ||
                    status.cancelled_reason === "superseded"
                  ? "The session ended before your payment completed. " +
                    "You have NOT been charged."
                  : "This payment was cancelled before it completed. " +
                    "You have NOT been charged.",
            });
            return false;
          }
          case "failed":
            void trackPaymentEvent(
              checkout.sessionId,
              "payment_confirm_failed",
              paymentOutcomeProperties(eventContext(checkout.sessionId, checkout.checkoutId), {
                reason: "capture_failed",
                ...outcomeMoney,
              }),
            );
            setPhase({
              phase: "failed",
              title: "Payment failed",
              message:
                "Your payment could not be completed and you have NOT been charged. " +
                "Please try again with a different payment method.",
            });
            return false;
          case "refund_due":
          case "refund_failed_manual":
          case "refunded":
            void trackPaymentEvent(
              checkout.sessionId,
              "payment_refunded",
              paymentOutcomeProperties(
                eventContext(checkout.sessionId, checkout.checkoutId),
                outcomeMoney,
              ),
            );
            setPhase({
              phase: "failed",
              title: "Session ended",
              message:
                "The session ended before your payment completed. " +
                "Your payment is being refunded in full.",
            });
            return false;
          default:
            return true; // pending/authorized/capturing: keep polling
        }
      },
      onGiveUp: () => {
        void trackPaymentEvent(
          checkout.sessionId,
          "payment_pending_timeout",
          paymentOutcomeProperties(eventContext(checkout.sessionId, checkout.checkoutId)),
        );
        setPhase({
          phase: "failed",
          title: "We lost track of this payment",
          message:
            "We couldn't confirm the outcome. If the table didn't start, you have NOT " +
            "been charged. Refresh this page to check again.",
        });
      },
    });

    return stop;
  }, [phase, preview, paidSessionIdFromUrl, eventContext, trackInteraction]);

  const handleAuthorized = useCallback(() => {
    if (phase.phase !== "form") return;
    const { checkout } = phase;
    // Nudge the server (idempotent); the pending poll retries it if this is lost.
    void apiClient["/api/v1/user/checkouts/{id}/authorized"]
      ["post"]({
        parameters: { id: checkout.checkoutId },
        body: { checkout_token: tokenRef.current },
      })
      .catch(() => null);
    setNotice(null);
    setPhase({ phase: "pending", checkout });
  }, [phase]);

  const handleStripeError = useCallback(
    (message: string) => {
      if (phase.phase !== "form") return;
      void trackPaymentEvent(
        phase.checkout.sessionId,
        "payment_submit_failed",
        paymentOutcomeProperties(
          eventContext(phase.checkout.sessionId, phase.checkout.checkoutId),
          {
            source: "stripe",
            message,
          },
        ),
      );
    },
    [phase, paidSessionIdFromUrl],
  );

  /**
   * Move an existing checkout onto a different tier. The highlight leads so the
   * tap feels instant and falls back to revertTo if the switch does not take —
   * the caller owns that value because the checkout's own claim-time rate goes
   * stale as soon as one switch succeeds.
   */
  const repriceCheckout = useCallback(
    async (checkout: Checkout, rateId: string, revertTo: string) => {
      if (!deviceInfo) return;
      const selected = deviceInfo.pricing_options.find((opt) => opt.id === rateId);
      setSelectedRateId(rateId);
      setRateSwitching(true);
      setRateError(null);
      trackInteraction(checkout.sessionId, checkout.checkoutId, "tier_selected", {
        interaction_target: rateId,
        amount_pence: selected?.amount_pence ?? null,
        duration_minutes: selected?.duration_minutes ?? null,
        currency: selected?.currency ?? null,
      });

      try {
        const res = await apiClient["/api/v1/user/checkouts/{id}/select-rate"]["post"]({
          parameters: { id: checkout.checkoutId },
          body: { checkout_token: tokenRef.current, session_rate_id: rateId },
        });

        setRateSwitching(false);

        if (res.code !== 200) {
          setSelectedRateId(revertTo);
          const body = res.body as { message?: string };
          setRateError(body?.message ?? "Could not switch to this option.");
          return;
        }

        setAmountVersion((v) => v + 1);
      } catch {
        setRateSwitching(false);
        setSelectedRateId(revertTo);
        setRateError("Network error. Please try again.");
      }
    },
    [deviceInfo, trackInteraction],
  );

  const onSelectTier = useCallback(
    async (rateId: string) => {
      if (!deviceInfo || rateId === selectedRateId) return;
      if (paying) return; // price is locked once a payment attempt starts

      if (preview) {
        setSelectedRateId(rateId);
        return;
      }

      if (phase.phase !== "form") {
        // There is no checkout to re-price yet, so hold the choice: the pending
        // claim prefers it, and the form phase reconciles whatever it gets back.
        chosenRateRef.current = rateId;
        setSelectedRateId(rateId);
        return;
      }

      await repriceCheckout(phase.checkout, rateId, selectedRateId ?? phase.checkout.sessionRateId);
    },
    [phase, deviceInfo, selectedRateId, paying, preview, repriceCheckout],
  );

  // A tier picked before the checkout existed — or while the claim was still in
  // flight — is re-priced the moment there is a checkout to re-price. It goes
  // straight to repriceCheckout because the claim response has already moved the
  // highlight onto its own rate, which is exactly what onSelectTier would treat
  // as nothing to do.
  useEffect(() => {
    if (phase.phase !== "form" || !deviceInfo) return;
    const chosen = chosenRateRef.current;
    chosenRateRef.current = null;
    if (!chosen || chosen === phase.checkout.sessionRateId) return;
    if (!knownRateId(deviceInfo, chosen)) return;
    void repriceCheckout(phase.checkout, chosen, phase.checkout.sessionRateId);
  }, [phase, deviceInfo, repriceCheckout]);

  const selectedOption =
    deviceInfo && selectedRateId
      ? deviceInfo.pricing_options.find((o) => o.id === selectedRateId)
      : undefined;
  const payAmountPence = selectedOption?.amount_pence ?? 0;
  const payCurrency = selectedOption?.currency ?? "gbp";

  const noticeBanner = notice ? (
    <p
      data-testid="pay-notice"
      className="mb-4 rounded-xl border border-border bg-surface-raised px-4 py-3 text-sm text-text-secondary"
    >
      {notice}
    </p>
  ) : null;

  if (phase.phase === "fatal") {
    return (
      <main
        className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center font-sans"
        data-testid="pay-fatal"
      >
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-text-primary mb-2">Oops</h1>
        <p className="text-text-secondary max-w-sm">{phase.message}</p>
        {phase.retryable ? (
          <button
            type="button"
            data-testid="pay-fatal-retry"
            onClick={() => window.location.reload()}
            className="mt-6 px-6 py-3 rounded-xl bg-surface-raised border border-border text-text-primary font-semibold"
          >
            Start again
          </button>
        ) : null}
      </main>
    );
  }

  if (phase.phase === "failed") {
    return (
      <main
        className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center font-sans"
        data-testid="payment-failed"
      >
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-text-primary mb-2">{phase.title}</h1>
        <p className="text-text-secondary max-w-sm">{phase.message}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 px-6 py-3 rounded-xl bg-surface-raised border border-border text-text-primary font-semibold"
        >
          Start again
        </button>
      </main>
    );
  }

  if (phase.phase === "success") {
    return (
      <main
        className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center font-sans"
        data-testid="payment-success"
      >
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
          <svg
            className="w-10 h-10 text-green-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-text-primary mb-2">
          {phase.isExtension ? "Time added!" : "You're all set!"}
        </h1>
        <p className="text-text-secondary max-w-md">
          {phase.isExtension
            ? "Extra time has been added to your session."
            : "The table is unlocked. Enjoy your game!"}
        </p>
        <div className="mt-6">
          <p className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
            Confirmation code
          </p>
          <p className="text-4xl font-bold tabular-nums text-[#a89cff] mt-1">
            {phase.verificationPhrase || phase.verificationCode}
          </p>
          <p className="text-xs text-text-muted mt-2">Check this matches the table</p>
        </div>
      </main>
    );
  }

  if (phase.phase === "free_play") {
    return (
      <main
        className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center font-sans"
        data-testid="free-play-notice"
      >
        {noticeBanner}
        <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6 text-4xl">
          🎉
        </div>
        <h1 className="text-2xl font-bold text-text-primary mb-2">Free play is on!</h1>
        <p className="text-text-secondary max-w-md">
          Games are free at {deviceInfo?.venue_name ?? "this venue"} right now — no payment needed.
          Just tap the table to start playing.
        </p>
      </main>
    );
  }

  if (phase.phase === "blocked") {
    return (
      <main
        className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center font-sans"
        data-testid="pay-blocked"
      >
        {noticeBanner}
        <Spinner />
        <h1 className="text-xl font-bold text-text-primary mt-6 mb-2">
          {phase.reason === "activating"
            ? "A payment is being finalized"
            : phase.reason === "updating"
              ? "This table is updating"
              : "Too many people are paying for this table"}
        </h1>
        <p className="text-text-secondary max-w-sm">
          {phase.reason === "updating"
            ? "The table is installing an update and will be back in a moment. This page will update automatically when it is ready."
            : "Hang tight — this page will update automatically when the table is ready."}
        </p>
      </main>
    );
  }

  if (phase.phase === "pending") {
    return (
      <main
        className="flex flex-col items-center justify-center min-h-[80vh] p-6 text-center font-sans"
        data-testid="payment-pending"
      >
        <Spinner />
        <h1 className="text-xl font-bold text-text-primary mt-6 mb-2">Confirming your payment</h1>
        <p className="text-text-secondary max-w-sm">
          {pendingSlow
            ? "This is taking longer than usual. Keep this page open — if the payment can't be completed you will NOT be charged."
            : "Waking up the table… this usually takes a few seconds."}
        </p>
      </main>
    );
  }

  if (!deviceInfo || !selectedRateId) {
    return (
      <main className="flex flex-col items-center justify-center min-h-[80vh] p-6 font-sans">
        {noticeBanner}
        <Spinner />
        <p className="text-text-secondary mt-4">Loading...</p>
      </main>
    );
  }

  const renderPaymentSection = (checkout: Checkout) => {
    return (
      <div data-testid="pay-form">
        {rateSwitching ? (
          <div className="flex flex-col items-center gap-3 py-6 rounded-2xl bg-surface-raised border border-border">
            <Spinner />
            <p className="text-text-secondary text-sm">Updating payment…</p>
          </div>
        ) : isEmulatorMode ? (
          <EmulatorPaymentForm
            amountPence={payAmountPence}
            currency={payCurrency}
            clientSecret={checkout.clientSecret}
            layoutPreview={preview}
            onAuthorized={handleAuthorized}
            onStripeError={handleStripeError}
            onProcessingChange={setPaying}
            trackInteraction={(t) => trackInteraction(checkout.sessionId, checkout.checkoutId, t)}
          />
        ) : (
          <Elements
            key={checkout.clientSecret}
            stripe={stripePromise}
            options={{ clientSecret: checkout.clientSecret, appearance }}
          >
            <PaymentForm
              amountPence={payAmountPence}
              currency={payCurrency}
              amountVersion={amountVersion}
              onAuthorized={handleAuthorized}
              onStripeError={handleStripeError}
              onProcessingChange={setPaying}
              trackInteraction={(t) => trackInteraction(checkout.sessionId, checkout.checkoutId, t)}
            />
          </Elements>
        )}
      </div>
    );
  };

  return (
    <main className="flex flex-col items-center px-5 pt-8 pb-14 min-h-screen font-sans">
      <div className="w-full max-w-md flex flex-col gap-6">
        {noticeBanner}
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-lg font-bold text-text-primary leading-tight">
              {deviceInfo.venue_name}
            </p>
            <p className="text-sm text-text-secondary mt-1">{deviceInfo.device_name}</p>
          </div>
          <div className="shrink-0 text-right pr-1">
            <p className="text-2xl font-bold leading-none text-[#a89cff]">
              {deviceInfo.verification_phrase}
            </p>
            <p className="text-center text-xs font-medium tracking-wide text-text-secondary mt-2">
              Confirmation code
            </p>
          </div>
        </div>

        {phase.phase === "form" && phase.checkout.mode === "extension" && (
          <p className="text-center text-xs text-text-muted uppercase tracking-widest">
            Adding time to your session
          </p>
        )}

        <div className="flex flex-col gap-3">
          {deviceInfo.pricing_options.map((opt) => {
            const selected = selectedRateId === opt.id;
            const flag = badgeImageSrc(opt.badge);
            const flagAtBottom = opt.badge === "best_value";
            return (
              <label
                key={opt.id}
                className={`relative block cursor-pointer rounded-2xl border-2 px-4 py-3.5 transition-colors overflow-visible ${
                  selected
                    ? "border-accent bg-surface-overlay"
                    : "border-border hover:border-border-subtle bg-surface-raised/80"
                } ${rateSwitching || paying ? "pointer-events-none opacity-70" : ""}`}
              >
                <input
                  type="radio"
                  name="session-tier"
                  className="sr-only"
                  checked={selected}
                  onChange={() => void onSelectTier(opt.id)}
                  disabled={rateSwitching || paying}
                />
                {flag ? (
                  <img
                    src={flag}
                    alt=""
                    className={`absolute right-0 z-10 h-auto w-[var(--pay-tier-flag-width)] max-w-[45%] pointer-events-none select-none ${
                      flagAtBottom ? "bottom-0 top-auto" : "top-0"
                    }`}
                  />
                ) : null}
                <div
                  className={`relative z-0 flex min-h-[2.75rem] items-center gap-3 ${
                    flag ? "pr-[calc(var(--pay-tier-flag-width)+0.5rem)]" : ""
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                      selected ? "border-accent bg-accent" : "border-text-muted"
                    }`}
                  >
                    {selected ? <span className="h-2 w-2 rounded-full bg-white" /> : null}
                  </span>
                  <span
                    className={`text-base font-sans ${selected ? "font-bold text-text-primary" : "text-text-secondary"}`}
                  >
                    {formatPrice(opt.amount_pence, opt.currency)} for {opt.duration_minutes} mins
                  </span>
                </div>
              </label>
            );
          })}
        </div>

        {rateError ? (
          <p className="text-red-400 text-sm text-center" role="alert">
            {rateError}
          </p>
        ) : null}

        {phase.phase === "form" ? (
          renderPaymentSection(phase.checkout)
        ) : (
          <div
            data-testid="payment-loading"
            className="flex flex-col items-center gap-3 py-6 rounded-2xl bg-surface-raised border border-border"
          >
            <Spinner />
            <p className="text-text-secondary text-sm">Preparing payment…</p>
          </div>
        )}
      </div>
    </main>
  );
}
